/**
 * Sprint 19: Notification Settings Page
 * 
 * Allows users to configure notification preferences including:
 * - Agentic PM notifications (Assign/Approve/Instruct)
 * - Sync notifications (ADO/Jira)
 * - Email digest preferences
 */

import React, { useState, useEffect } from 'react';
import { 
  Bell, BellOff, Mail, MessageSquare, CheckCircle, 
  AlertCircle, Bot, Users, Shield, Save, RefreshCw
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';

const NotificationSettingsPage = () => {
  const [preferences, setPreferences] = useState({
    agentic_pm_enabled: true,
    agentic_pm_assign: true,
    agentic_pm_approve: true,
    agentic_pm_instruct: true,
    sync_notifications: true,
    email_digest: false,
    email_digest_frequency: 'daily',
    browser_notifications: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await api.getNotificationPreferences();
      if (response.data?.preferences) {
        setPreferences(prev => ({ ...prev, ...response.data.preferences }));
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
      // Use defaults
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await api.updateNotificationPreferences(preferences);
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const Toggle = ({ enabled, onToggle, disabled = false }) => (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? 'bg-[var(--clr-brand-primary)]' : 'bg-gray-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      data-testid="notification-toggle"
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--clr-brand-primary)]" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="notification-settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
          <Bell className="w-7 h-7 text-[var(--clr-brand-primary)]" />
          Notification Settings
        </h1>
        <p className="text-[var(--clr-text-secondary)] mt-1">
          Configure how you receive notifications from ScopeOps
        </p>
      </div>

      {/* Agentic PM Notifications */}
      <div className="bg-[var(--clr-surface-card)] rounded-xl border border-[var(--clr-border-light)] p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Bot className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Agentic PM Notifications</h2>
              <p className="text-sm text-[var(--clr-text-secondary)]">Real-time alerts for PM actions</p>
            </div>
          </div>
          <Toggle
            enabled={preferences.agentic_pm_enabled}
            onToggle={() => handleToggle('agentic_pm_enabled')}
          />
        </div>

        {preferences.agentic_pm_enabled && (
          <div className="space-y-4 pl-14 border-l-2 border-purple-200 ml-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-[var(--clr-text-primary)]">Task Assignments</p>
                <p className="text-sm text-[var(--clr-text-muted)]">When a task is assigned to you</p>
              </div>
              <Toggle
                enabled={preferences.agentic_pm_assign}
                onToggle={() => handleToggle('agentic_pm_assign')}
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-[var(--clr-text-primary)]">Approval Decisions</p>
                <p className="text-sm text-[var(--clr-text-muted)]">When items you submitted are approved/rejected</p>
              </div>
              <Toggle
                enabled={preferences.agentic_pm_approve}
                onToggle={() => handleToggle('agentic_pm_approve')}
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-[var(--clr-text-primary)]">PM Instructions</p>
                <p className="text-sm text-[var(--clr-text-muted)]">When you receive instructions from a PM</p>
              </div>
              <Toggle
                enabled={preferences.agentic_pm_instruct}
                onToggle={() => handleToggle('agentic_pm_instruct')}
              />
            </div>
          </div>
        )}
      </div>

      {/* Sync Notifications */}
      <div className="bg-[var(--clr-surface-card)] rounded-xl border border-[var(--clr-border-light)] p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <RefreshCw className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Sync Notifications</h2>
              <p className="text-sm text-[var(--clr-text-secondary)]">ADO/Jira integration events</p>
            </div>
          </div>
          <Toggle
            enabled={preferences.sync_notifications}
            onToggle={() => handleToggle('sync_notifications')}
          />
        </div>
      </div>

      {/* Browser Notifications */}
      <div className="bg-[var(--clr-surface-card)] rounded-xl border border-[var(--clr-border-light)] p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <MessageSquare className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Browser Notifications</h2>
              <p className="text-sm text-[var(--clr-text-secondary)]">Show toast notifications in-app</p>
            </div>
          </div>
          <Toggle
            enabled={preferences.browser_notifications}
            onToggle={() => handleToggle('browser_notifications')}
          />
        </div>
      </div>

      {/* Email Digest */}
      <div className="bg-[var(--clr-surface-card)] rounded-xl border border-[var(--clr-border-light)] p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Mail className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Email Digest</h2>
              <p className="text-sm text-[var(--clr-text-secondary)]">Receive summary emails (requires SendGrid activation)</p>
            </div>
          </div>
          <Toggle
            enabled={preferences.email_digest}
            onToggle={() => handleToggle('email_digest')}
          />
        </div>

        {preferences.email_digest && (
          <div className="pl-14 ml-4">
            <label className="text-sm font-medium text-[var(--clr-text-primary)]">Frequency</label>
            <select
              value={preferences.email_digest_frequency}
              onChange={(e) => setPreferences(prev => ({ ...prev, email_digest_frequency: e.target.value }))}
              className="mt-1 block w-48 px-3 py-2 border border-[var(--clr-border-light)] rounded-lg text-sm"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="realtime">Real-time (each event)</option>
            </select>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={savePreferences}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-[var(--clr-brand-primary)] text-white font-medium rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50 transition-colors"
          data-testid="save-notification-prefs"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Preferences
        </button>
      </div>
    </div>
  );
};

export default NotificationSettingsPage;
